import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import cn from 'classnames'
import { generatePath } from 'react-router-dom'
import arrayMutators from 'final-form-arrays'
import { useSelector } from 'react-redux'
import notify from '../../utilities/notify'
import useMutation from '../../hooks/useMutation'
import { RadioInput } from '../RadioInput'
import styles from './new-requests.module.scss'
import useQuery from '../../hooks/useQuery'
import { formatPhoneNumber } from '../../utilities/stringUtilities'
import QUERY_KEYS from '../../graphql/queryKeys'
import createClientMutation from '../../graphql/mutations/createClient'
import confirmServiceRequestMutation from '../../graphql/mutations/confirmServiceRequest'
import createJob from '../../graphql/mutations/createJob'
import clientsQuery from '../../graphql/queries/clients'
import jobSearchQuery from '../../graphql/queries/jobsSearch'
import { captureErrorAndNotify, handleValidateAndNotify } from '../../utilities/errorHandlers'
import { TERMS_AND_CONDITION_WARNING_KIND } from '../../utilities/termsAndConditionWarnings'
import useTermsAndConditionExpiredWarning from '../terms-and-condition-expired-warning-modal/useTermsAndConditionExpiredWarning'
import ClientCreateForm from '../client-create-form'
import NewJobForm from '../new-job-form'
import { Spinner } from '../../pages/shared/Spinner'
import { ROUTES } from '../../routes'
import { NO_TC_AGREEMENT_VALUE } from '../../pages/create-terms-and-condition-agreement/constants'

export default function ServiceRequestConfirm ({
  request,
  closeModal,
  refetchServiceRequests
}) {
  const [formStep, setFormStep] = useState(null)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedJob, setSelectedJob] = useState(null)
  const [createdTickets, setCreatedTickets] = useState(null)
  const [ticketCreateRetryKey, setTicketCreateRetryKey] = useState(0)
  const promptedTicketCreateForJobId = useRef(null)
  const startedTicketCreateForJobId = useRef(null)
  const service = request[0]

  const { user, hauler } = useSelector(({ user }) => ({

    user: user.user,
    hauler: user.hauler
  }))

  const { data: { clients }, isFetching: fetchingAccounts } = useQuery([
    QUERY_KEYS.clients,
    user.haulerId,
    { column: 'customId', direction: 'DESC' },
    `${service.firstName} ${service.lastName}`
  ],
  clientsQuery,
  {
    enabled: Boolean(user.haulerId),
    placeholderData: { clients: { nodes: [] } }
  })

  const { data: { jobs }, isFetching: fetchingJobsList } = useQuery([
    QUERY_KEYS.jobsSearch,
    user.haulerId,
    25,
    { column: 'customId', direction: 'DESC' },
    {
      clientId: selectedAccount?.id,
      streetAddress: service.addressline1,
      city: service.city,
      zipCode: service.zipCode,
      status: 'open'
    }
  ],
  jobSearchQuery,
  {
    enabled: formStep === 'JOB_SEARCH' && selectedAccount != null,
    placeholderData: { jobs: { nodes: [] } },
    onError (error) {
      captureErrorAndNotify(error, 'Error fetching jobs')
    }
  })

  const { mutate: createClient, isLoading: createClientLoading } = useMutation(createClientMutation, {
    onSuccess ({ client }) {
      setSelectedAccount(client)
      setFormStep('JOB_CREATE')
      notify('success', 'Account Created Successfully')
    },
    onError () {
      captureErrorAndNotify('error', 'Failed to Create Account')
    }
  })

  const { mutate: confirmServiceRequest, isLoading: confirmingServiceRequest } = useMutation(confirmServiceRequestMutation, {
    onSuccess ({ tickets }) {
      setCreatedTickets(tickets)
    },
    onError (error) {
      startedTicketCreateForJobId.current = null
      promptedTicketCreateForJobId.current = null
      handleValidateAndNotify(error, 'Failed to Create Tickets for Order')
    }
  })

  const { mutate: createJobMutation, isLoading: createJobLoading } = useMutation(createJob, {
    onSuccess ({ job }) {
      setSelectedJob(job)
    },
    onError () {
      notify('error', 'Failed to create job')
    }
  })

  const { warnOrContinue, warningModal } = useTermsAndConditionExpiredWarning(
    selectedAccount,
    TERMS_AND_CONDITION_WARNING_KIND.TICKET
  )

  useEffect(() => {
    if (createdTickets != null) {
      setFormStep('TICKETS_CREATED')
    }
  }, [createdTickets, setFormStep])

  useEffect(() => {
    if (selectedJob == null || createdTickets != null) return
    if (startedTicketCreateForJobId.current === selectedJob.id) return
    if (promptedTicketCreateForJobId.current === selectedJob.id) return

    promptedTicketCreateForJobId.current = selectedJob.id
    warnOrContinue(() => {
      startedTicketCreateForJobId.current = selectedJob.id
      confirmServiceRequest({
        orderId: service.orderId,
        jobId: selectedJob.id
      })
    })
  }, [selectedJob, confirmServiceRequest, service, createdTickets, warnOrContinue, ticketCreateRetryKey])

  useEffect(() => {
    if (fetchingJobsList) {
      return
    }

    if (formStep === 'JOB_SEARCH' && jobs.nodes.length !== 0) {
      setFormStep('JOB_SELECT')
    } else if (formStep === 'JOB_SEARCH' && jobs.nodes.length === 0) {
      setFormStep('JOB_CREATE')
    }
  }, [formStep, jobs, fetchingJobsList])

  useEffect(() => {
    if (clients == null) return
    if (clients?.nodes?.length === 0) {
      setFormStep('ACCOUNT_CREATE')
    } else {
      setFormStep('ACCOUNT_SEARCH')
    }
  }, [clients])

  function closeAfterTicketCreate () {
    closeModal()
    refetchServiceRequests()
  }

  function handleCreateClient (vals) {
    const { tcAgreementType, ...clientFields } = vals
    delete clientFields.navToJob
    if (tcAgreementType && tcAgreementType !== NO_TC_AGREEMENT_VALUE) {
      clientFields.selectedTermsAndConditionId = tcAgreementType
    }
    if (clientFields.primaryPhoneExtension) {
      clientFields.primaryPhoneExtension = parseInt(clientFields.primaryPhoneExtension)
    }
    createClient({ haulerId: user.haulerId, navToJob: false, clientFields })
  }

  function handleCreateJob (newJob) {
    delete newJob.displayAddress
    createJobMutation({
      ...newJob,
      latitude: parseFloat(newJob.latitude),
      longitude: parseFloat(newJob.longitude),
      clientId: selectedAccount.id
    })
  }

  async function handleSelectAccount (val) {
    const acct = clients.nodes.find(el => el.id.toString() === val.selection)

    await setSelectedAccount(acct)
    await setFormStep('JOB_SEARCH')
  }
  function handleSelectJob (val) {
    const job = jobs.nodes.find(el => el.id.toString() === val.selection)

    promptedTicketCreateForJobId.current = null
    startedTicketCreateForJobId.current = null
    setSelectedJob(job)
    setTicketCreateRetryKey((key) => key + 1)
  }

  if (fetchingAccounts || formStep == null) {
    return (
      <Spinner isFetching />
    )
  }

  function selectValidation (values) {
    const errors = {}

    if (!values.selection) {
      errors.selection = 'Select one before proceeding'
    }

    return errors
  }

  function getNewJobsInitialValues () {
    const addlDetails = {
      addressline1: service.addressline1,
      city: service.city,
      state: service.state,
      zip: service.zip,
      country: 'US',
      addressDetails: service.addressDetails,
      displayAddress: `${service.addressline1} ${service.city} ${service.state} ${service.zip}`,
      latitude: service.latitude,
      longitude: service.longitude,
      firstName: service.firstName,
      lastName: service.lastName,
      prettyPrimaryPhone: formatPhoneNumber(service.primaryPhone, true),
      email: service.email
    }

    return {
      ...selectedAccount,
      ...addlDetails,
      notes: service.serviceNotes ?? null
    }
  }

  function truncateString (str, limit = 50) {
    if (!str) return 'None'

    if (str.length > 50) {
      return `${str.slice(0, limit)} ...`
    }

    return str
  }
  function formatJobLocation () {
    const job = selectedJob?.addressline1 ? selectedJob : service
    const cityLine = `${job.city || 'unknown'}, ${job.state || 'unknown'} ${job.zip || 'unknown'}`
    const details = job.addressDetails ? ` (${job.addressDetails})` : ''
    return `${job.addressline1 || 'unknown'}${details}, ${cityLine}`
  }
  function getAccountLabel () {
    return selectedAccount?.name ||
      (service.firstName || service.lastName ? `${service.firstName || ''} ${service.lastName || ''}`.trim() : null) ||
      `Order #${service.orderId}`
  }
  return (
    <div className={cn(styles.modifyConfirmModalContainer)}>
      {warningModal}
      { formStep === 'ACCOUNT_CREATE' && (
        <>
          <header className={cn(styles.modalHeader)}>
            <h3>Create New Account</h3>
            <button
              className={cn(styles.closeModalBtn, 'dis-btn dis-btn-no-shadow')}
              type='button'
              onClick={closeModal}>
              <i className={cn(styles.closeModalIcon, 'material-icons dis-btn-icon')}>close</i>
            </button>
          </header>
          <ClientCreateForm
            onSubmit={handleCreateClient}
            isFetching={createClientLoading}
            onlyCreate={true}
            saveAcctOnlyText='Create New Account'
            initialValues={{
              name: `${service.firstName} ${service.lastName}`,
              firstName: service.firstName,
              lastName: service.lastName,
              email: service.email,
              primaryPhone: formatPhoneNumber(service.primaryPhone, true),
              addressline1: service.addressline1,
              city: service.city,
              state: service.state,
              zip: service.zip
            }}
          />
        </>
      )}
      { formStep === 'ACCOUNT_SEARCH' && (
        <>
          <header className={cn(styles.modalHeader)}>
            <p>At least 1 account matching the first and last name of the customer was found. Select an account to use or create a new one.</p>
            <button
              className={cn(styles.closeModalBtn, 'dis-btn dis-btn-no-shadow')}
              type='button'
              onClick={closeModal}>
              <i className={cn(styles.closeModalIcon, 'material-icons dis-btn-icon')}>close</i>
            </button>
          </header>
          <Form onSubmit={handleSelectAccount} mutators={{ ...arrayMutators }} validate={selectValidation}>
            {({ handleSubmit, invalid }) => (
              <form onSubmit={handleSubmit} autoComplete='off' className={cn()}>
                <div className={cn(styles.optionSelectGroupWrapper)}>
                  {clients.nodes.map(client => {
                    const contactName = client.firstName || client.lastName ? `${client.firstName || ''} ${client.lastName || ''}` : 'None'
                    return (
                      <Field
                        key={client.id}
                        classNames={cn(styles.optionSelectGroup, 'dis-panel')}
                        name='selection'
                        radioVal={client.id}
                        label={(<span>
                          {client.name}, <br />Contact Name: {contactName} <br /> id: {client.customId}
                        </span>)}
                        component={RadioInput}
                      />
                    )
                  })}
                </div>

                <div className={cn(styles.serviceRequestModifyButtons)}>
                  <button
                    type='submit'
                    disabled={invalid}
                    className={cn('dis-btn dis-btn-lg dis-btn-primary')}>
                    Select this Account
                    <i className='material-icons dis-btn-icon'>check</i>
                  </button>
                  <button type='button'
                    className={cn('dis-btn dis-btn-lg dis-btn-primary')}
                    onClick={() => setFormStep('ACCOUNT_CREATE')}>
                    Create New Account
                    <i className='material-icons dis-btn-icon'>edit</i>
                  </button>
                </div>
              </form>
            )}
          </Form>
        </>
      )}
      {(formStep === 'JOB_SELECT') && (
        <>
          {confirmingServiceRequest
            ? (
              <p>waiting...</p>
              )
            : (
              <>
                <header className={cn(styles.modalHeader)}>
                  <p>At least 1 jobs matching the first name and last name of the customer was found. Select a job to use or create a new one.</p>
                  <button
                    className={cn(styles.closeModalBtn, 'dis-btn dis-btn-no-shadow')}
                    type='button'
                    onClick={closeModal}>
                    <i className={cn(styles.closeModalIcon, 'material-icons dis-btn-icon')}>close</i>
                  </button>
                </header>
                <Form onSubmit={handleSelectJob} mutators={{ ...arrayMutators }} validate={selectValidation}>
                  {({ handleSubmit, invalid }) => (
                    <form onSubmit={handleSubmit} autoComplete='off' className={cn()}>
                      <div className={cn(styles.optionSelectGroupWrapper)}>
                        {jobs.nodes.map(job => {
                          const contactName = job.firstName || job.lastName ? `${job.firstName || ''} ${job.lastName || ''}` : 'None'
                          let address = `${job.addressline1} ${job.city} ${job.state} ${job.zip}`
                          if (job.addressDetails) address += ` (${job.addressDetails})`
                          return (
                            <Field
                              key={job.id}
                              classNames={cn(styles.optionSelectGroup, 'dis-panel')}
                              name='selection'
                              radioVal={job.id}
                              label={(<span>
                                {address}
                                <br />
                                Contact Name: {contactName} {job.prettyPrimaryPhone}
                                <br />
                                Job ID: {job.customId}
                                <br />
                                Job Notes: {truncateString(job.notes)}
                              </span>)}
                              component={RadioInput}
                            />
                          )
                        })}
                      </div>
                      <div className={cn(styles.serviceRequestModifyButtons)}>
                        <button
                          type='submit'
                          disabled={invalid}
                          className={cn('dis-btn dis-btn-lg dis-btn-primary')}>
                          Select this Job
                          <i className='material-icons dis-btn-icon'>check</i>
                        </button>
                        <button type='button'
                          className={cn('dis-btn dis-btn-lg dis-btn-primary')}
                          onClick={() => setFormStep('JOB_CREATE')}>
                          Create New Job
                          <i className='material-icons dis-btn-icon'>edit</i>
                        </button>
                      </div>
                    </form>
                  )}
                </Form>
              </>
              )}
        </>
      )}
      {formStep === 'JOB_CREATE' && (
        <>
          {confirmingServiceRequest
            ? (
              <p>waiting...</p>
              )
            : (
              <div>
                <header className={cn(styles.modalHeader)}>
                  <h3>Create Job for Order <br />{getAccountLabel()}</h3>
                  <button
                    className={cn(styles.closeModalBtn, 'dis-btn dis-btn-no-shadow')}
                    type='button'
                    onClick={closeModal}>
                    <i className={cn(styles.closeModalIcon, 'material-icons dis-btn-icon')}>close</i>
                  </button>
                </header>
                <NewJobForm
                  onlyCreate={true}
                  onSubmit={handleCreateJob}
                  disabled={createJobLoading}
                  initialValues={getNewJobsInitialValues()}
                  defaultCoordinates={{ latitude: hauler.latitude, longitude: hauler.longitude }}
                />
              </div>
              )
          }
        </>
      )}
      {formStep === 'TICKETS_CREATED' && (
        <div>
          <header className={cn(styles.modalHeader)}>
            <h4>Ticket(s) created for <br />{getAccountLabel()}</h4>
          </header>
          <div className={cn(styles.createdSummary)}>
            <p>
              <strong>Job Location:</strong> {formatJobLocation()}
            </p>
            <div>
              <strong>Ticket(s):</strong>
              <ul>
                {request.map((svc) => (
                  <li key={svc.id}>
                    {svc.ticketType.name} - {svc.resourceType.name} ({svc.ticketType.shortCode}-{svc.resourceType.shortCode})
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className={cn(styles.serviceRequestModifyButtons)}>
            <div>
              {createdTickets.map(ticket => (
                <a
                  className={cn('dis-btn dis-btn-sm dis-btn-primary')}
                  href={generatePath(ROUTES.ticket, { id: ticket.id })}
                  key={ticket.id}
                  target='blank'
                  rel='noopener noreferrer'>
                  View Ticket/Add Fees
                </a>
              ))}
            </div>
            <button
              className='dis-btn dis-btn-lg dis-btn-alert'
              type='button'
              onClick={closeAfterTicketCreate}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

ServiceRequestConfirm.propTypes = {
  request: PropTypes.array.isRequired,
  closeModal: PropTypes.func.isRequired,
  refetchServiceRequests: PropTypes.func.isRequired
}
